import { matchRoutes, type RouteObject } from 'react-router';

import type { RoutePage } from '@/hooks/folio-page-context-contract';

import { PAGE_REGISTRY, type RegisteredPage } from './page-registry';

// One route per registered pattern; React Router ranks them, so `/settings`
// wins over `/:tenantSlug` and the `*` catch-all only takes what nothing else does.
const REGISTERED_ROUTES: RouteObject[] = [
  ...new Set(PAGE_REGISTRY.map((page) => page.route.pattern)),
].map((pattern) => ({ path: pattern }));

function satisfiesRequiredQuery(page: RegisteredPage, searchParams: URLSearchParams): boolean {
  const required = page.route.requiredQuery;
  return (
    required !== undefined &&
    Object.entries(required).every(([key, value]) => searchParams.get(key) === value)
  );
}

function findRegisteredPage(pathname: string, search: string): RegisteredPage | undefined {
  const pattern = matchRoutes(REGISTERED_ROUTES, pathname)?.at(-1)?.route.path;
  if (pattern === undefined) {
    return undefined;
  }
  const candidates = PAGE_REGISTRY.filter((page) => page.route.pattern === pattern);
  const searchParams = new URLSearchParams(search);
  return candidates.find((page) => satisfiesRequiredQuery(page, searchParams)) ?? candidates[0];
}

/**
 * The registered page for a location, as Sean should see it before the page
 * registers its own context. `null` for routes without Sean (404s, redirects).
 */
export function resolveRoutePage(pathname: string, search: string): RoutePage | null {
  const page = findRegisteredPage(pathname, search);
  if (page?.folioPage === undefined) {
    return null;
  }
  return { page: page.folioPage, page_id: page.pageId, capabilities: page.pageCapabilities };
}
